import { useEffect, useState, createContext, useContext, type ReactNode } from 'react';
import { apiCalls } from './api.ts';

// Minimal i18n: matches the legacy's `tDict` pattern. Light, no extra dep.
export type Lang = 'en' | 'fr';

type Dict = Record<string, unknown>;

const FALLBACK: Record<Lang, Dict> = {
  en: {
    header: { statusByService: 'Status by service :', actionsHint: 'Actions' },
    legend: { none: 'No status yet', ok: 'OK (200)', incident: 'Incident (500)' },
    ui: {
      details: 'Details',
      close: 'Close',
      activeIncidents: 'Active incidents',
      none: 'None',
      slot: 'Selected slot',
      past7: 'Past incidents (7d)',
    },
  },
  fr: {
    header: { statusByService: 'Status by service :', actionsHint: 'Actions' },
    legend: { none: 'No status yet', ok: 'OK (200)', incident: 'Incident (500)' },
    ui: {
      details: 'Détails',
      close: 'Fermer',
      activeIncidents: 'Incidents actifs',
      none: 'Aucun',
      slot: 'Slot sélectionné',
      past7: 'Past incidents (7j)',
    },
  },
};

export interface I18nContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (path: string, fallback?: string) => string;
}

const Ctx = createContext<I18nContextValue | null>(null);

const LANG_KEY = 'incidents_lang';

export function getInitialLang(defaultLang: Lang): Lang {
  try {
    const fromStorage = localStorage.getItem(LANG_KEY) as Lang | null;
    if (fromStorage === 'en' || fromStorage === 'fr') return fromStorage;
  } catch {
    /* ignore */
  }
  if (typeof navigator !== 'undefined' && navigator.language?.startsWith('fr')) return 'fr';
  if (typeof navigator !== 'undefined' && navigator.language?.startsWith('en')) return 'en';
  return defaultLang;
}

function lookup(dict: Dict | null, path: string): string | undefined {
  if (!dict) return undefined;
  let cur: unknown = dict;
  for (const p of path.split('.')) {
    if (cur && typeof cur === 'object' && p in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[p];
    } else {
      return undefined;
    }
  }
  return typeof cur === 'string' ? cur : undefined;
}

export function I18nProvider({ initialLang, children }: { initialLang: Lang; children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang);
  const [dict, setDict] = useState<Dict | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiCalls
      .fetchI18n(lang)
      .then((d) => {
        if (!cancelled) setDict(d as Dict);
      })
      .catch(() => {
        if (!cancelled) setDict(FALLBACK[lang]);
      });
    try {
      localStorage.setItem(LANG_KEY, lang);
    } catch {
      /* ignore */
    }
    document.documentElement.lang = lang;
    return () => {
      cancelled = true;
    };
  }, [lang]);

  const setLang = (l: Lang) => setLangState(l);

  const t = (path: string, fb?: string) =>
    lookup(dict, path) ?? lookup(FALLBACK[lang], path) ?? fb ?? path;

  return <Ctx.Provider value={{ lang, setLang, t }}>{children}</Ctx.Provider>;
}

export function useI18n(): I18nContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useI18n outside provider');
  return v;
}
